import { useCallback, useEffect, useState } from "preact/hooks";

import {
  createCadence,
  type CreateCadenceBody,
  fetchCadences,
  patchCadence,
  type PatchCadenceBody,
  previewCadence,
} from "../lib/api";
import type { CadenceOut, CadencePreviewOut } from "../types";

/**
 * Sprint-004 B4 — the Chores screen's data + mutations. `cadences` is
 * every rhythm/state from `GET /api/v1/cadences` (admin/manager/api) —
 * including paused ones, so the admin can find and resume a paused chore.
 */
export function useAdminCadences(enabled: boolean) {
  const [cadences, setCadences] = useState<CadenceOut[] | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setError(null);
    try {
      const rows = await fetchCadences();
      setCadences(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the chore list.");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (body: Omit<CreateCadenceBody, "idempotency_key">): Promise<CadenceOut> => {
      const result = await createCadence({ ...body, idempotency_key: crypto.randomUUID() });
      await refresh();
      return result;
    },
    [refresh],
  );

  const patch = useCallback(
    async (
      cadenceId: string,
      body: Omit<PatchCadenceBody, "idempotency_key">,
    ): Promise<CadenceOut> => {
      const result = await patchCadence(cadenceId, {
        ...body,
        idempotency_key: crypto.randomUUID(),
      });
      await refresh();
      return result;
    },
    [refresh],
  );

  const preview = useCallback((cadenceId: string): Promise<CadencePreviewOut> => {
    return previewCadence(cadenceId);
  }, []);

  return { cadences, loading, error, refresh, create, patch, preview };
}
